/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import {
  font,
  weightMedium,
  weightSemiBold,
  weightBold,
} from 'src/components/css'
import { sectionCardStyles } from '../styles/shared'
import {
  useAiSuggestKpis,
  type AiSuggestKpiPayload,
} from '../hooks/useAiSuggestKpis'

const containerStyles = css`
  ${sectionCardStyles};
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const headerStyles = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`

const titleStyles = css`
  ${font.body};
  font-weight: ${weightSemiBold};
  font-size: 14px;
  color: ${colors.WHITE};
  margin: 0;
`

const subTitleStyles = css`
  ${font.body};
  font-size: 11px;
  color: ${colors.NEUTRAL_70};
`

const kpiGridStyles = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`

const metricBoxStyles = css`
  background-color: ${colors.SECONDARY_300};
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const metricLabelStyles = css`
  ${font.body};
  font-size: 10px;
  font-weight: ${weightMedium};
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const metricValueStyles = css`
  ${font.body};
  font-size: 18px;
  font-weight: ${weightBold};
  color: ${colors.WHITE};
`

const headlineMetricBoxStyles = css`
  ${metricBoxStyles};
  background-color: ${colors.PRIMARY}1A;
  border: 1px solid ${colors.PRIMARY};
`

const breakdownRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 12px;
  background-color: ${colors.SECONDARY_300};
  border-radius: 4px;
  font-size: 12px;
  color: ${colors.WHITE};
  ${font.body};
`

const breakdownLabelStyles = css`
  font-weight: ${weightMedium};
`

const emptyStateStyles = css`
  ${font.body};
  font-size: 12px;
  color: ${colors.NEUTRAL_70};
  font-style: italic;
  padding: 16px;
  text-align: center;
`

const errorStyles = css`
  ${font.body};
  background-color: ${colors.ERROR_300}1A;
  border: 1px solid ${colors.ERROR_300};
  border-radius: 4px;
  color: ${colors.ERROR_300};
  padding: 8px 12px;
  font-size: 12px;
`

const formatPct = (value: number): string => {
  // value is 0..1 range
  return `${Math.round(value * 100)}%`
}

const formatSeconds = (value: number | null): string => {
  if (value === null || value === 0) {
    return '—'
  }
  if (value < 60) {
    return `${Math.round(value)}s`
  }
  return `${Math.round(value / 60)}m`
}

interface BreakdownProps {
  kpis: AiSuggestKpiPayload
}

const InheritedBreakdown: React.FC<BreakdownProps> = ({ kpis }) => {
  const { specialized, inherited } = kpis.by_inherited
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 4px;
      `}
    >
      <span css={metricLabelStyles}>Specialized vs inherited (§11)</span>
      <div css={breakdownRowStyles}>
        <span css={breakdownLabelStyles}>Specialized</span>
        <span>
          {specialized.count} suggestions ·{' '}
          {formatPct(specialized.unchanged_acceptance_rate)} unchanged accept
        </span>
      </div>
      <div css={breakdownRowStyles}>
        <span css={breakdownLabelStyles}>Inherited from ancestor</span>
        <span>
          {inherited.count} suggestions ·{' '}
          {formatPct(inherited.unchanged_acceptance_rate)} unchanged accept
        </span>
      </div>
    </div>
  )
}

const PolicyOrgBreakdown: React.FC<BreakdownProps> = ({ kpis }) => {
  const entries = Object.entries(kpis.by_policy_org_uid)
  if (entries.length === 0) {
    return null
  }
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 4px;
      `}
    >
      <span css={metricLabelStyles}>By policy org</span>
      {entries.map(([orgUid, stats]) => (
        <div css={breakdownRowStyles} key={orgUid}>
          <span css={breakdownLabelStyles}>{orgUid}</span>
          <span>
            {stats.count} · {formatPct(stats.unchanged_acceptance_rate)}{' '}
            unchanged
          </span>
        </div>
      ))}
    </div>
  )
}

interface AiSuggestKpiCardProps {
  /** Hide the card entirely on a fresh install where no events exist yet. */
  hideWhenEmpty?: boolean
}

/**
 * Phase 4 — KPI dashboard tile for the AI Suggest feature.
 *
 * Renders against `GET /scheduler/ai-suggest/kpis`. Phase 1+2 ship this
 * tile against zeros (the endpoint returns the zero-shaped payload until
 * customers use the feature); Phase 4 adds real aggregations server-side.
 *
 * Headline metric is `unchanged_acceptance_rate` — the IRAP success
 * metric (≥80% post-training, per `IRAP-AI-RL-LAYER.md` §10 Phase 5
 * acceptance).
 *
 * Breakdowns required by §11:
 *   - by_policy_org_uid — which level of the tree is producing accepted
 *     vs rejected suggestions.
 *   - by_inherited — separate the rate for flights that received a
 *     sub-org-specific artifact vs flights that inherited an ancestor's
 *     artifact. Without this split, the headline metric is dominated by
 *     inherited results.
 */
export const AiSuggestKpiCard: React.FC<AiSuggestKpiCardProps> = ({
  hideWhenEmpty = false,
}) => {
  'use memo'

  const { kpis, isLoading, error } = useAiSuggestKpis()

  if (isLoading && kpis === null) {
    return (
      <div css={containerStyles}>
        <div css={emptyStateStyles}>Loading KPIs...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div css={containerStyles}>
        <div css={errorStyles}>Could not load KPIs: {error.message}</div>
      </div>
    )
  }

  if (!kpis) {
    return null
  }

  if (hideWhenEmpty && kpis.total_suggestions === 0) {
    return null
  }

  return (
    <div css={containerStyles} data-testid='ai-suggest-kpi-card'>
      <div css={headerStyles}>
        <h3 css={titleStyles}>AI Suggest performance</h3>
        <span css={subTitleStyles}>
          last {kpis.window_days}d · {kpis.total_responded} responded /{' '}
          {kpis.total_suggestions} shown
        </span>
      </div>

      <div css={kpiGridStyles}>
        <div css={headlineMetricBoxStyles} title='IRAP success metric'>
          <span css={metricLabelStyles}>Accepted unchanged</span>
          <span css={metricValueStyles}>
            {formatPct(kpis.unchanged_acceptance_rate)}
          </span>
        </div>
        <div css={metricBoxStyles}>
          <span css={metricLabelStyles}>Acceptance rate</span>
          <span css={metricValueStyles}>{formatPct(kpis.acceptance_rate)}</span>
        </div>
        <div css={metricBoxStyles}>
          <span css={metricLabelStyles}>Rejection rate</span>
          <span css={metricValueStyles}>{formatPct(kpis.rejection_rate)}</span>
        </div>
        <div css={metricBoxStyles}>
          <span css={metricLabelStyles}>7-day regret</span>
          <span css={metricValueStyles}>
            {formatPct(kpis.seven_day_regret_rate)}
          </span>
        </div>
        <div css={metricBoxStyles}>
          <span css={metricLabelStyles}>Median time to decide</span>
          <span css={metricValueStyles}>
            {formatSeconds(kpis.median_time_to_decide_seconds)}
          </span>
        </div>
      </div>

      <InheritedBreakdown kpis={kpis} />
      <PolicyOrgBreakdown kpis={kpis} />
    </div>
  )
}

export default AiSuggestKpiCard
